// frontend/src/components/EmailMessageModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-11-24
// Updated:      2026-09-10 14:44 BST
//
// Description:
//   Fully independent, portalled modal for EMAIL conversations linked to a Job.
//   • Portals straight to document.body with fixed overlay (cannot be clipped by JobEditModal).
//   • Top half: recent email messages for the given job (all conversations, scoped to job).
//   • Bottom half: rich email composer with WYSIWYG-style editor (Tiptap), attachments and signatures.
//   • Clicking "View" opens EmailConversationModal.
//
// Patch (2025-12-22):
//   ✅ Thread history fix:
//      - If jobId is missing but conversationId exists (common when launched from a conversation row),
//        load history directly from /api/conversations/{id}/messages so the thread still displays.
//
// Messaging Consolidation (2026-09-10):
//   • Explicit conversationId is authoritative in EVERY context, including Job context.
//     A selected history row therefore opens that exact email thread rather than silently
//     falling back to a wider job/contact/customer history.
//   • Added explicit customerId support and preserves the hierarchy:
//       Job -> Contact -> Customer, or Contact -> Customer when no Job is present.
//   • With no explicit conversationId, recent Email history is loaded from the strongest
//     available scope using the canonical flattened conversation endpoints.
//   • Existing Job history fallbacks are retained behind the canonical route so the
//     established Job workflow remains backward-compatible during consolidation.
//   • Conversation creation/reuse includes all known job/contact/customer associations.
//   • Parent onSend payload now includes contactId and customerId in addition to the
//     existing Job/conversation data; existing parent handlers may ignore the extra keys.
//   • No tenant_id is accepted, generated or sent by this component.
//
// Mobile Responsiveness (2026-09-10):
//   • Main modal uses the mobile viewport safely (100dvh) without clipping beneath the shell.
//   • Header/context text wraps on narrow screens and the close target is 44px on mobile.
//   • Recent-message rows stack safely and keep View touch-safe.
//   • Composer fields, HTML toggle, attachments, signature controls and footer actions
//     reflow into a single-column mobile layout while desktop layout remains unchanged.
//   • Signature Manager receives the same narrow-screen/touch-target hardening.
//   • No duplicate mobile component tree is mounted.

import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";
import TiptapEmailEditor from "./TiptapEmailEditor.jsx";
import EmailConversationModal from "./EmailConversationModal.jsx";

const SIGNATURE_STORAGE_KEY = "anpr_email_signatures_v1";
const SIGNATURE_DEFAULT_INDEX_KEY = "anpr_email_signatures_default_index_v1";

// Hard z-index (inline, not Tailwind) so this modal always wins.
const MODAL_Z = 2147483647;

// ---------------- Helpers ----------------

const getRowJobId = (m) =>
  m?.job_id ?? m?.jobId ?? m?.conversation_job_id ?? m?.conversation?.job_id ?? null;

const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

const formatTimestamp = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const safeArr = (data) => {
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  return [];
};

const getConversationRowId = (row) => row?.id ?? row?.conversation_id ?? row?.conversationId ?? null;

// Normalise initial attachments to a File[] (ignore junk)
const normaliseInitialAttachments = (v) => {
  if (!Array.isArray(v)) return [];
  return v.filter((x) => x instanceof File);
};

// ---------------- Signature Manager (child modal) ----------------

const SignatureManagerModal = ({
  open,
  onClose,
  signatures,
  onChangeSignatures,
  defaultIndex,
  onChangeDefaultIndex,
  theme,
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [name, setName] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    if (!open) {
      setEditingIndex(null);
      setName("");
      setContent("");
    }
  }, [open]);

  const startNew = () => {
    setEditingIndex(null);
    setName("");
    setContent("");
  };

  const startEdit = (index) => {
    const sig = signatures[index];
    setEditingIndex(index);
    setName(sig.name || "");
    setContent(sig.html || "");
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const next = [...signatures];
    const payload = { name: name.trim(), html: content || "" };
    if (editingIndex == null) next.push(payload);
    else next[editingIndex] = payload;
    onChangeSignatures(next);
    setEditingIndex(null);
    setName("");
    setContent("");
  };

  const handleDelete = (index) => {
    const next = signatures.filter((_, i) => i !== index);
    onChangeSignatures(next);

    if (editingIndex === index) {
      setEditingIndex(null);
      setName("");
      setContent("");
    }

    if (defaultIndex === index) onChangeDefaultIndex(null);
    else if (defaultIndex != null && index < defaultIndex) onChangeDefaultIndex(defaultIndex - 1);
  };

  const handleDefaultChange = (e) => {
    const val = e.target.value;
    if (val === "") onChangeDefaultIndex(null);
    else onChangeDefaultIndex(parseInt(val, 10));
  };

  if (!open) return null;

  const { font_body: fb, font_header: fh } = theme || {};
  const fontBody = fb || "system-ui";
  const fontHeader = fh || fontBody;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 p-2 sm:p-4"
      style={{ zIndex: MODAL_Z }}
      onMouseDown={onClose}
    >
      <div
        className="relative flex h-[calc(100dvh-16px)] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-lg sm:h-auto sm:max-h-[80vh]"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ fontFamily: fontBody, zIndex: MODAL_Z }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
          <h3 className="text-base font-semibold" style={{ fontFamily: fontHeader }}>
            Manage Signatures
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 flex-none touch-manipulation items-center justify-center rounded-full bg-btnGeneralNormal text-lg leading-none text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:h-8 sm:w-8"
          >
            ×
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-auto overscroll-contain px-4 py-3 sm:px-5">
          <div>
            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs font-semibold text-gray-700">Saved signatures</span>
              <button
                type="button"
                onClick={startNew}
                className="min-h-[44px] w-full touch-manipulation rounded-full bg-btnGeneralNormal px-4 py-2 text-xs font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
              >
                New signature
              </button>
            </div>

            {signatures.length === 0 && (
              <div className="text-xs text-gray-500">You don&apos;t have any saved signatures yet.</div>
            )}

            {signatures.length > 0 && (
              <div className="space-y-2">
                {signatures.map((sig, idx) => (
                  <div
                    key={`${sig.name}-${idx}`}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 px-3 py-2 text-xs sm:flex-row sm:items-start sm:justify-between"
                  >
                    <div className="flex-1">
                      <div className="font-semibold mb-0.5">{sig.name || "(no name)"}</div>
                      <div
                        className="text-gray-700"
                        style={{ maxHeight: "3.2em", overflow: "hidden", fontFamily: fontBody }}
                        dangerouslySetInnerHTML={{ __html: sig.html || "<em>(empty)</em>" }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-col sm:gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(idx)}
                        className="min-h-[44px] touch-manipulation rounded-full border border-gray-300 px-3 py-2 text-[11px] hover:bg-gray-100 sm:min-h-0 sm:px-2 sm:py-0.5"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(idx)}
                        className="min-h-[44px] touch-manipulation rounded-full border border-red-200 px-3 py-2 text-[11px] text-red-600 hover:bg-red-50 sm:min-h-0 sm:px-2 sm:py-0.5"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-3">
            <div className="mb-2 text-xs font-semibold text-gray-700">
              {editingIndex == null ? "New signature" : "Edit signature"}
            </div>
            <div className="space-y-2">
              <div>
                <label className="block text-[11px] text-gray-600 mb-1">Name</label>
                <input
                  className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm sm:min-h-0 sm:py-1.5 sm:text-xs"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Leon – Default"
                />
              </div>

              <div>
                <label className="block text-[11px] text-gray-600 mb-1">Content (HTML or plain text)</label>
                <textarea
                  className="min-h-[140px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm sm:min-h-[120px] sm:py-1.5 sm:text-xs"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="<p>Best regards,<br/>Leon</p>"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-3">
            <div className="mb-1 text-xs font-semibold text-gray-700">Default signature for new messages</div>
            <select
              className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm sm:min-h-0 sm:py-1.5 sm:text-xs"
              value={defaultIndex != null && defaultIndex >= 0 ? String(defaultIndex) : ""}
              onChange={handleDefaultChange}
            >
              <option value="">None</option>
              {signatures.map((sig, idx) => (
                <option key={`${sig.name}-${idx}`} value={idx}>
                  {sig.name || `Signature ${idx + 1}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 border-t border-gray-200 px-4 py-3 sm:flex sm:justify-end sm:px-5">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] touch-manipulation rounded bg-btnGeneralNormal px-4 py-2 text-xs font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="min-h-[44px] touch-manipulation rounded bg-btnActionNormal px-4 py-2 text-xs font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionClick"
          >
            Save Signature
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------- Main Modal ----------------

const EmailMessageModal = ({
  open,
  onClose,
  jobId,
  jobRef = "",
  defaultTo = "",
  conversationId: initialConversationId = null,
  messages: propMessages = [],
  fromAddress = "",
  fromDisplayName = "",
  location = "",
  actionLabel = "",
  signatureHtml = "",
  onSend,
  job,
  contactId,
  customerId,
  contactEmail,
  messageId = null,

  // ✅ NEW: allow parent to pre-seed attachments (e.g. Quote PDF)
  initialAttachments = [],
}) => {
  const { theme } = useTheme() || {};

  const [jobMeta, setJobMeta] = useState(null);
  const [resolvedContact, setResolvedContact] = useState(null);

  const [signatures, setSignatures] = useState([]);
  const [selectedSignatureIndex, setSelectedSignatureIndex] = useState(null);
  const [defaultSignatureIndex, setDefaultSignatureIndex] = useState(null);
  const [signatureManagerOpen, setSignatureManagerOpen] = useState(false);

  const [bodyHtml, setBodyHtml] = useState("");
  const [isHtml, setIsHtml] = useState(true);
  const [attachments, setAttachments] = useState([]);

  const [loadedMessages, setLoadedMessages] = useState([]);

  const [conversationId, setConversationId] = useState(initialConversationId ?? null);
  const [isSending, setIsSending] = useState(false);

  const [activeConversationId, setActiveConversationId] = useState(null);

  const { font_body: fb, font_header: fh } = theme || {};
  const fontBody = fb || "system-ui";
  const fontHeader = fh || fontBody;

  useEffect(() => {
    setConversationId(initialConversationId ?? null);
  }, [initialConversationId]);

  const availableFonts = useMemo(() => {
    const base = [];
    if (theme?.font_body) base.push(theme.font_body);
    if (theme?.font_header && theme.font_header !== theme.font_body) base.push(theme.font_header);
    base.push("Roboto", "Montserrat", "Arial", "Georgia", "Times New Roman");
    return Array.from(new Set(base));
  }, [theme]);

  const messages = useMemo(() => {
    if (Array.isArray(loadedMessages) && loadedMessages.length > 0) return loadedMessages;
    return Array.isArray(propMessages) ? propMessages : [];
  }, [propMessages, loadedMessages]);

  useEffect(() => {
    if (!open || !jobId) return;

    let cancelled = false;

    api
      .get(`/api/jobs/${jobId}`)
      .then((res) => {
        if (cancelled) return;
        setJobMeta(res.data || null);
        // eslint-disable-next-line no-console
        console.log("[EmailMessageModal] job meta for job", jobId, res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[EmailMessageModal] failed to load job meta", err);
      });

    return () => {
      cancelled = true;
    };
  }, [open, jobId]);

  const effectiveContactId = useMemo(() => {
    if (contactId) return contactId;
    if (job && job.contact_id) return job.contact_id;
    if (jobMeta && jobMeta.contact_id) return jobMeta.contact_id;
    return null;
  }, [contactId, job, jobMeta]);

  useEffect(() => {
    if (!open) return;
    if (!effectiveContactId) {
      setResolvedContact(null);
      return;
    }

    let cancelled = false;

    api
      .get(`/api/contacts/${effectiveContactId}`)
      .then((res) => {
        if (cancelled) return;
        setResolvedContact(res.data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedContact(null);
      });

    return () => {
      cancelled = true;
    };
  }, [open, effectiveContactId]);

  const apiContactEmail = useMemo(() => {
    if (!resolvedContact) return "";
    const rc = resolvedContact;

    const candidates = [
      rc.email,
      rc.email_address,
      rc.primary_email,
      rc.work_email,
      rc.personal_email,
      rc.contact_email,
    ];

    for (const c of candidates) {
      if (typeof c === "string" && c.trim()) return c.trim();
    }
    return "";
  }, [resolvedContact]);

  const effectiveCustomerId = useMemo(() => {
    if (customerId != null) return customerId;

    if (resolvedContact) {
      const rc = resolvedContact;
      if (rc.customer_id != null) return rc.customer_id;
      if (rc.customerId != null) return rc.customerId;
    }

    if (jobMeta) {
      const jm = jobMeta;
      if (jm.customer_id != null) return jm.customer_id;
      if (jm.client_id != null) return jm.client_id;
    }

    if (job) {
      const j = job;
      if (j.customer_id != null) return j.customer_id;
      if (j.client_id != null) return j.client_id;
    }

    return null;
  }, [customerId, resolvedContact, jobMeta, job]);

  const deriveConversationIdFromMessages = () => {
    if (!Array.isArray(propMessages) || propMessages.length === 0) return null;
    const withConv = propMessages.find((m) => m.conversation_id != null || m.conversationId != null);
    if (!withConv) return null;
    return withConv.conversation_id ?? withConv.conversationId ?? null;
  };

  const effectiveConversationId = useMemo(() => {
    if (conversationId != null) return conversationId;
    return deriveConversationIdFromMessages();
  }, [conversationId, propMessages]);

  // ----------------------------
  // Thread/history loader
  // ----------------------------
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    const normaliseEmailMessageRow = (
      m,
      forcedConversationId = null,
      forcedJobId = null
    ) => {
      const job_id = forcedJobId ?? getRowJobId(m);

      const directionKey =
        m && typeof m === "object"
          ? Object.keys(m).find((k) =>
              k.toLowerCase().includes("direction")
            )
          : null;

      const rawDirection =
        m.direction ??
        m.last_direction ??
        m.lastDirection ??
        m.message_direction ??
        m.messageDirection ??
        m.direction_type ??
        m.directionType ??
        (directionKey ? m[directionKey] : "");

      const direction =
        rawDirection && typeof rawDirection === "string"
          ? rawDirection.toLowerCase()
          : rawDirection || "unknown";

      const subject =
        m.subject ||
        m.message_subject ||
        m.conversation_subject ||
        m.title ||
        "";

      const body_html =
        m.body_html ||
        m.html_body ||
        m.bodyHtml ||
        "";

      const body_text =
        m.body_text ||
        m.text_body ||
        m.bodyText ||
        m.body ||
        "";

      const previewCandidate =
        m.preview ||
        m.body_preview ||
        body_text ||
        stripHtml(body_html) ||
        (m.body && String(m.body)) ||
        "";

      const preview = previewCandidate
        ? String(previewCandidate).slice(0, 200)
        : "";

      const created_at =
        m.created_at ||
        m.sent_at ||
        m.received_at ||
        m.last_message_at ||
        null;

      const from =
        m.from_display ||
        m.last_message_from ||
        m.from ||
        m.from_address ||
        m.sender ||
        "";

      const toVal =
        m.to_display ||
        m.last_message_to ||
        m.to ||
        m.to_address ||
        m.recipient ||
        "";

      const conv =
        forcedConversationId ??
        (m.conversation_id ?? m.conversationId ?? null);

      return {
        id:
          m.id ??
          m.message_id ??
          `${conv || "no-conv"}-${created_at || "no-ts"}-${Math.random()}`,
        conversation_id: conv,
        job_id,
        contact_id:
          m.contact_id ??
          m.contactId ??
          effectiveContactId ??
          null,
        customer_id:
          m.customer_id ??
          m.customerId ??
          effectiveCustomerId ??
          null,
        channel: "email",
        direction,
        subject,
        preview,
        created_at,
        status:
          m.delivery_status ||
          m.conversation_status ||
          m.status ||
          "",
        conversation_status:
          m.conversation_status ||
          m.status ||
          "",
        body_html,
        body_text,
        from,
        to: toVal,
      };
    };

    const normaliseRows = (
      rows,
      forcedConversationId = null,
      forcedJobId = null
    ) =>
      safeArr(rows)
        .filter((m) => {
          const channel = String(m?.channel || "email").toLowerCase();
          return channel === "email";
        })
        .map((m) =>
          normaliseEmailMessageRow(
            m,
            forcedConversationId,
            forcedJobId
          )
        )
        .sort((a, b) => {
          const da = a.created_at
            ? new Date(a.created_at).getTime()
            : 0;
          const db = b.created_at
            ? new Date(b.created_at).getTime()
            : 0;
          return db - da;
        });

    const load = async () => {
      try {
        // Explicit conversation selection is ALWAYS authoritative.
        if (effectiveConversationId) {
          const { data } = await api.get(
            `/api/conversations/${effectiveConversationId}/messages`
          );

          if (!cancelled) {
            setLoadedMessages(
              normaliseRows(
                data,
                effectiveConversationId,
                jobId ?? null
              )
            );
          }
          return;
        }

        // Strongest available hierarchy scope: Job -> Contact -> Customer.
        const scopedEndpoint = jobId
          ? `/api/conversations/job/${jobId}/messages`
          : effectiveContactId
          ? `/api/conversations/contact/${effectiveContactId}/messages`
          : effectiveCustomerId
          ? `/api/conversations/customer/${effectiveCustomerId}/messages`
          : null;

        if (scopedEndpoint) {
          try {
            const { data } = await api.get(scopedEndpoint, {
              params: { channel: "email" },
            });

            if (!cancelled) {
              setLoadedMessages(
                normaliseRows(
                  data,
                  null,
                  jobId ?? null
                )
              );
            }
            return;
          } catch (e) {
            // Only the historical Job path has legacy fallbacks worth preserving.
            if (!jobId || e?.response?.status !== 404) {
              throw e;
            }
          }
        }

        if (!jobId) {
          if (!cancelled) setLoadedMessages([]);
          return;
        }

        // Legacy Job fallback 1: /api/jobs/{jobId}/messages
        try {
          const { data } = await api.get(`/api/jobs/${jobId}/messages`);
          const arr = safeArr(data);

          if (arr.length) {
            const scoped = arr.filter((m) => {
              const jid = getRowJobId(m);
              return jid == null
                ? true
                : Number(jid) === Number(jobId);
            });

            if (!cancelled) {
              setLoadedMessages(
                normaliseRows(scoped, null, jobId)
              );
            }
            return;
          }
        } catch (e) {
          if (e?.response?.status !== 404) throw e;
        }

        // Legacy Job fallback 2: /api/messages?job_id=
        try {
          const { data } = await api.get("/api/messages", {
            params: {
              job_id: jobId,
              limit: 500,
            },
          });

          const scoped = safeArr(data).filter((m) => {
            const jid = getRowJobId(m);
            return jid == null
              ? true
              : Number(jid) === Number(jobId);
          });

          if (scoped.length) {
            if (!cancelled) {
              setLoadedMessages(
                normaliseRows(scoped, null, jobId)
              );
            }
            return;
          }
        } catch (e) {
          if (e?.response?.status !== 404) throw e;
        }

        // Legacy Job fallback 3: list conversations, then fetch each thread.
        const { data: convData } = await api.get("/api/conversations", {
          params: {
            job_id: jobId,
            channel: "email",
            limit: 200,
          },
        });

        const convIds = safeArr(convData)
          .map((c) => getConversationRowId(c))
          .filter(Boolean);

        if (!convIds.length) {
          if (!cancelled) setLoadedMessages([]);
          return;
        }

        const fetchOne = async (cid) => {
          try {
            const { data: msgData } = await api.get(
              `/api/conversations/${cid}/messages`
            );
            return normaliseRows(msgData, cid, jobId);
          } catch {
            return [];
          }
        };

        const all = [];
        for (let i = 0; i < convIds.length; i += 6) {
          // eslint-disable-next-line no-await-in-loop
          const chunkResults = await Promise.all(
            convIds.slice(i, i + 6).map(fetchOne)
          );
          chunkResults.forEach((rows) => all.push(...rows));
        }

        all.sort((a, b) => {
          const da = a.created_at
            ? new Date(a.created_at).getTime()
            : 0;
          const db = b.created_at
            ? new Date(b.created_at).getTime()
            : 0;
          return db - da;
        });

        if (!cancelled) {
          setLoadedMessages(all);
        }
      } catch (e) {
        console.warn(
          "[EmailMessageModal] failed to load email history",
          {
            jobId,
            contactId: effectiveContactId,
            customerId: effectiveCustomerId,
            conversationId: effectiveConversationId,
            status: e?.response?.status,
            detail: e?.response?.data || e,
          }
        );

        if (!cancelled) {
          setLoadedMessages([]);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    jobId,
    effectiveContactId,
    effectiveCustomerId,
    effectiveConversationId,
  ]);

  const effectiveJobRef = useMemo(() => {
    if (jobRef) return jobRef;
    if (job && job.job_ref) return job.job_ref;
    if (jobMeta && jobMeta.job_ref) return jobMeta.job_ref;

    if (Array.isArray(messages) && messages.length > 0) {
      const withRef = messages.find((m) => m.job_ref);
      if (withRef?.job_ref) return withRef.job_ref;
    }
    return "";
  }, [jobRef, job, jobMeta, messages]);

  const effectiveJobId = useMemo(() => {
    if (jobId) return jobId;
    if (job && job.id) return job.id;
    if (jobMeta && jobMeta.id) return jobMeta.id;
    return null;
  }, [jobId, job, jobMeta]);

  const historyScopeLabel = useMemo(() => {
    if (effectiveConversationId) return "this conversation";
    if (effectiveJobId) return "this job";
    if (effectiveContactId) return "this contact";
    if (effectiveCustomerId) return "this customer";
    return "this context";
  }, [
    effectiveConversationId,
    effectiveJobId,
    effectiveContactId,
    effectiveCustomerId,
  ]);

  const contextSummary = useMemo(() => {
    const parts = [];

    if (effectiveJobId) {
      parts.push(`Job ID: ${effectiveJobId}`);
    }

    if (effectiveJobRef) {
      parts.push(`Job Ref: ${effectiveJobRef}`);
    }

    if (effectiveContactId) {
      parts.push(`Contact ID: ${effectiveContactId}`);
    }

    if (effectiveCustomerId) {
      parts.push(`Customer ID: ${effectiveCustomerId}`);
    }

    if (effectiveConversationId) {
      parts.push(`Conversation: ${effectiveConversationId}`);
    }

    if (messageId) {
      parts.push(`Message: ${messageId}`);
    }

    return parts;
  }, [
    effectiveJobId,
    effectiveJobRef,
    effectiveContactId,
    effectiveCustomerId,
    effectiveConversationId,
    messageId,
  ]);

  const derivedDefaultTo = useMemo(() => {
    if (defaultTo) return defaultTo;
    if (contactEmail && contactEmail.trim()) return contactEmail.trim();
    if (apiContactEmail) return apiContactEmail;

    const list = Array.isArray(messages) ? [...messages] : [];
    if (!list.length) return "";

    const lastOutbound = [...list].reverse().find(
      (m) => (m.direction === "outbound" || m.direction_label === "outbound") && (m.to || m.to_display)
    );
    if (lastOutbound?.to || lastOutbound?.to_display) return (lastOutbound.to || lastOutbound.to_display).trim();

    const lastInbound = [...list].reverse().find(
      (m) => (m.direction === "inbound" || m.direction_label === "inbound") && (m.from || m.from_display)
    );
    if (lastInbound?.from || lastInbound?.from_display) return (lastInbound.from || lastInbound.from_display).trim();

    return "";
  }, [defaultTo, contactEmail, apiContactEmail, messages]);

  const computedDefaultSubject = useMemo(() => {
    const parts = [];
    if (effectiveJobRef) parts.push(`Job ${effectiveJobRef}`);
    if (location) parts.push(location);
    if (actionLabel) parts.push(actionLabel);
    return parts.join(" – ");
  }, [effectiveJobRef, location, actionLabel]);

  const fromDisplay = useMemo(() => {
    if (fromDisplayName && fromAddress) return `${fromDisplayName} <${fromAddress}>`;
    return fromAddress || fromDisplayName || "";
  }, [fromDisplayName, fromAddress]);

  const [to, setTo] = useState(derivedDefaultTo);
  const [subject, setSubject] = useState("");

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(SIGNATURE_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setSignatures(parsed);
      }
      const rawIdx = window.localStorage.getItem(SIGNATURE_DEFAULT_INDEX_KEY);
      if (rawIdx !== null && rawIdx !== "") {
        const idx = parseInt(rawIdx, 10);
        if (!Number.isNaN(idx)) setDefaultSignatureIndex(idx);
      }
    } catch {
      // ignore
    }
  }, []);

  const persistSignatures = (next) => {
    setSignatures(next);
    try {
      window.localStorage.setItem(SIGNATURE_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const handleSignaturesChanged = (next) => {
    persistSignatures(next);
    if (defaultSignatureIndex != null && (defaultSignatureIndex < 0 || defaultSignatureIndex >= next.length)) {
      setDefaultSignatureIndex(null);
      try {
        window.localStorage.removeItem(SIGNATURE_DEFAULT_INDEX_KEY);
      } catch {
        // ignore
      }
    }
  };

  const handleDefaultSignatureIndexChange = (idx) => {
    setDefaultSignatureIndex(idx);
    try {
      if (idx == null) window.localStorage.removeItem(SIGNATURE_DEFAULT_INDEX_KEY);
      else window.localStorage.setItem(SIGNATURE_DEFAULT_INDEX_KEY, String(idx));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!open) return;

    setTo(derivedDefaultTo || "");
    setSubject(computedDefaultSubject || "");
    setBodyHtml("");
    setIsHtml(true);

    // ✅ IMPORTANT: seed attachments from initialAttachments when modal opens
    setAttachments(normaliseInitialAttachments(initialAttachments));

    setActiveConversationId(null);

    if (defaultSignatureIndex != null && signatures[defaultSignatureIndex]) {
      const html = signatures[defaultSignatureIndex].html || "";
      setBodyHtml(html);
      setSelectedSignatureIndex(defaultSignatureIndex);
    } else if (signatureHtml) {
      setBodyHtml(signatureHtml);
      setSelectedSignatureIndex(null);
    } else {
      setBodyHtml("");
      setSelectedSignatureIndex(null);
    }
  }, [
    open,
    derivedDefaultTo,
    computedDefaultSubject,
    defaultSignatureIndex,
    signatures,
    signatureHtml,
    initialAttachments,
  ]);

  useEffect(() => {
    if (open) setTo((prev) => (prev ? prev : derivedDefaultTo || ""));
  }, [derivedDefaultTo, open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(files);
  };

  const handleInsertSignature = () => {
    let htmlToInsert = "";

    if (selectedSignatureIndex != null && signatures[selectedSignatureIndex])
      htmlToInsert = signatures[selectedSignatureIndex].html || "";
    else if (signatureHtml) htmlToInsert = signatureHtml;

    if (!htmlToInsert) return;

    setBodyHtml((prev) => {
      if (prev && prev.includes(htmlToInsert)) return prev;
      if (!prev) return htmlToInsert;
      return `${prev}<br/><br/>${htmlToInsert}`;
    });
  };

  const tryResolveLatestConversationId = async () => {
    try {
      const params = {
        job_id: effectiveJobId ?? undefined,
        contact_id: effectiveContactId ?? undefined,
        customer_id: effectiveCustomerId ?? undefined,
        channel: "email",
        limit: 50,
      };

      const { data } = await api.get("/api/conversations", { params });
      const arr = safeArr(data);

      if (!arr.length) return null;

      const sorted = [...arr].sort((a, b) => {
        const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });

      return getConversationRowId(sorted[0]) ?? null;
    } catch {
      return null;
    }
  };

  const handleSend = async () => {
    if (isSending) return;

    setIsSending(true);
    try {
      let convId = conversationId ?? effectiveConversationId ?? null;
      const subjectForApi = subject || computedDefaultSubject || "(no subject)";

      if (!convId) {
        const reuseId = await tryResolveLatestConversationId();
        if (reuseId) {
          convId = reuseId;
          setConversationId(reuseId);
          // eslint-disable-next-line no-console
          console.info("[EmailMessageModal] reusing existing conversation id", reuseId);
        }
      }

      if (!convId) {
        const convPayload = {
          job_id: effectiveJobId ?? null,
          customer_id: effectiveCustomerId,
          contact_id: effectiveContactId ?? null,
          subject: subjectForApi,
          channel: "email",
        };

        // eslint-disable-next-line no-console
        console.info("[EmailMessageModal] POST /api/conversations payload", convPayload);

        const res = await api.post("/api/conversations", convPayload);
        const newId = res?.data?.id ?? null;
        if (newId) {
          convId = newId;
          setConversationId(newId);
        }
      }

      const bodyTextForApi = isHtml ? stripHtml(bodyHtml) : bodyHtml || "";

      const parentPayload = {
        jobId: effectiveJobId ?? null,
        jobRef: effectiveJobRef || null,
        contactId: effectiveContactId ?? null,
        customerId: effectiveCustomerId ?? null,
        conversationId: convId ?? null,
        messageId: messageId ?? null,
        to,
        from: fromAddress || fromDisplay,
        subject,
        body: bodyHtml,
        isHtml,
        attachments,
      };

      // ✅ CRITICAL FIX:
      // If a parent onSend handler is provided, it is the ONLY place attachments can be reliably handled
      // (multipart / SendGrid etc). Do NOT also call the JSON /send endpoint afterwards because that path
      // has no attachment support and effectively "drops" them.
      if (onSend) {
        await onSend(parentPayload);
        onClose?.();
        return;
      }

      // Legacy/internal JSON send (no attachment support). Keep for backwards compatibility.
      if (convId != null) {
        const apiPayload = {
          conversation_id: convId,
          customer_id: effectiveCustomerId,
          channel: "email",
          subject: subjectForApi,
          body_text: bodyTextForApi,
          body_html: isHtml ? bodyHtml || null : null,
          to,
        };

        await api.post(`/api/conversations/${convId}/send`, apiPayload);
      }

      onClose?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[EmailMessageModal] send failed", e?.response?.status, e?.response?.data || e);
    } finally {
      setIsSending(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <style>{`
        .tiptap ul { list-style-type: disc; margin-left: 1.25rem; padding-left: 0.25rem; }
        .tiptap ol { list-style-type: decimal; margin-left: 1.25rem; padding-left: 0.25rem; }
        .tiptap li { margin-bottom: 0.1rem; }
        .tiptap h1 { font-size: 1.05rem; font-weight: 600; margin: 0.35rem 0; }
        .tiptap h2 { font-size: 0.98rem; font-weight: 600; margin: 0.3rem 0; }
        .tiptap h3 { font-size: 0.92rem; font-weight: 600; margin: 0.25rem 0; }
      `}</style>

      <div
        className="fixed inset-0 flex items-center justify-center bg-black/40 p-2 sm:p-4"
        style={{ zIndex: MODAL_Z }}
        onMouseDown={onClose}
      >
        <div
          className="relative flex h-[calc(100dvh-16px)] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-lg sm:h-auto sm:max-h-[90vh]"
          onMouseDown={(e) => e.stopPropagation()}
          style={{ fontFamily: fontBody, zIndex: MODAL_Z }}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-btnGeneralNormal text-lg leading-none text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:h-8 sm:w-8"
            aria-label="Close"
          >
            ×
          </button>

          <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 pb-3 pt-5 pr-16 sm:px-6 sm:pr-14">
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: fontHeader }}>
                Email Conversation
              </h3>
              <p className="mt-0.5 flex flex-wrap gap-x-2 gap-y-1 break-words text-xs text-gray-600">
                {contextSummary.length ? (
                  contextSummary.map((item) => (
                    <span key={item} className="font-mono font-medium">
                      {item}
                    </span>
                  ))
                ) : (
                  <span>No linked job/contact/customer context</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-3 pb-4 pt-3 sm:overflow-hidden sm:px-6 sm:pb-5">
            <div className="flex max-h-[36dvh] min-h-[180px] flex-none flex-col overflow-hidden rounded-lg border border-gray-200 bg-gray-50 sm:max-h-none sm:min-h-[220px] sm:flex-[3]">
              <div className="flex flex-col gap-1 border-b border-gray-200 bg-white px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="break-words text-xs text-gray-700">Recent messages for {historyScopeLabel}</div>
                <div className="break-words text-[11px] text-gray-500">Showing {messages?.length ?? 0}</div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {(!messages || messages.length === 0) && (
                  <div className="px-3 py-4 text-xs text-gray-500">No previous email messages for {historyScopeLabel} yet.</div>
                )}

                {messages &&
                  messages.map((msg) => {
                    const key = msg.id ?? msg.message_id ?? msg.created_at ?? Math.random();
                    const direction = msg.direction || msg.direction_label || "outbound";
                    const directionLabel = direction === "inbound" ? "Inbound" : "Outbound";
                    const createdLabel = formatTimestamp(msg.created_at);

                    const preview = msg.preview || msg.body_preview || msg.body_text || msg.body || "";
                    const shortPreview = preview && preview.length > 160 ? `${preview.slice(0, 160)}…` : preview;

                    const convId = msg.conversation_id ?? msg.conversationId ?? null;

                    return (
                      <div key={key} className="border-t border-gray-200 px-3 py-2 text-xs bg-white">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex-1">
                            <div className="mb-0.5 break-words text-[13px] font-semibold">{msg.subject || "(no subject)"}</div>
                            <div className="text-[11px] text-gray-500">
                              {direction === "inbound" ? "Received" : "Sent"}
                              {createdLabel ? ` · ${createdLabel}` : ""}
                              {convId && (
                                <>
                                  {" "}
                                  · Conv: <span className="font-mono">{convId}</span>
                                </>
                              )}
                            </div>

                            {shortPreview && (
                              <div
                                className="mt-1 text-[11px] text-gray-700"
                                style={{ maxHeight: "2.8em", overflow: "hidden" }}
                              >
                                {shortPreview}
                              </div>
                            )}
                          </div>

                          <div className="flex flex-row items-center justify-between gap-2 sm:flex-col sm:items-end sm:justify-start sm:gap-1">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                direction === "inbound"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-sky-50 text-sky-700 border border-sky-200"
                              }`}
                            >
                              {directionLabel}
                            </span>

                            <button
                              type="button"
                              onClick={() => {
                                const targetConvId = convId || effectiveConversationId || null;
                                if (targetConvId) setActiveConversationId(targetConvId);
                                else console.warn("[EmailMessageModal] View clicked but no conversation id available", msg);
                              }}
                              className="mt-1 min-h-[44px] touch-manipulation rounded-full bg-btnGeneralNormal px-4 py-2 text-[11px] font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:min-h-0 sm:px-3 sm:py-1.5"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="flex min-h-0 flex-none flex-col overflow-hidden rounded-lg border border-gray-200 bg-white sm:min-h-[260px] sm:flex-[2]">
              <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-gray-700 font-semibold">New Email</div>
                <div className="flex items-center gap-3 text-[11px] text-gray-600">
                  <label className="flex min-h-[44px] touch-manipulation items-center gap-2 sm:min-h-0">
                    <input type="checkbox" checked={isHtml} onChange={(e) => setIsHtml(e.target.checked)} className="h-5 w-5 sm:h-auto sm:w-auto" />
                    <span>Send as HTML</span>
                  </label>
                </div>
              </div>

              <div className="flex-1 overflow-auto px-3 py-3 space-y-3">
                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">From</label>
                  <input
                    type="text"
                    className="min-h-[44px] w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-sm sm:min-h-0 sm:py-1.5 sm:text-xs"
                    value={fromDisplay}
                    readOnly
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">To</label>
                  <input
                    type="email"
                    className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm sm:min-h-0 sm:py-1.5 sm:text-xs"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="contact@example.com"
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">Subject</label>
                  <input
                    className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm sm:min-h-0 sm:py-1.5 sm:text-xs"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Subject line"
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-600 mb-1">Body</label>
                  <TiptapEmailEditor value={bodyHtml} onChange={setBodyHtml} placeholder="" availableFonts={availableFonts} />
                </div>

                <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:flex-wrap sm:items-end">
                  <div className="min-w-0 w-full sm:w-auto">
                    <label className="block text-[11px] text-gray-600 mb-1">Attachments</label>
                    <input type="file" multiple onChange={handleAttachmentChange} className="min-h-[44px] w-full max-w-full touch-manipulation text-[11px] sm:min-h-0 sm:w-auto" />
                  </div>

                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1 text-[10px] text-gray-700">
                      {attachments.map((file, idx) => (
                        <span
                          key={`${file.name}-${idx}`}
                          className="px-2 py-0.5 rounded-full bg-gray-100 border border-gray-300"
                        >
                          {file.name}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex w-full flex-col gap-3 sm:ml-auto sm:w-auto sm:flex-row sm:items-end">
                    <div className="flex flex-col">
                      <label className="text-[11px] text-gray-600 mb-0.5">Signature</label>
                      <select
                        className="min-h-[44px] w-full min-w-0 rounded border border-gray-300 bg-white px-3 py-2 text-sm sm:min-h-0 sm:min-w-[180px] sm:w-auto sm:px-2 sm:py-1 sm:text-[11px]"
                        value={selectedSignatureIndex != null ? String(selectedSignatureIndex) : ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "") setSelectedSignatureIndex(null);
                          else setSelectedSignatureIndex(parseInt(val, 10));
                        }}
                      >
                        <option value="">{signatureHtml ? "Default (profile)" : "No signature selected"}</option>
                        {signatures.map((sig, idx) => (
                          <option key={`${sig.name}-${idx}`} value={idx}>
                            {sig.name || `Signature ${idx + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pb-0.5 sm:flex sm:items-center">
                      <button
                        type="button"
                        onClick={handleInsertSignature}
                        className="min-h-[44px] touch-manipulation rounded bg-btnGeneralNormal px-4 py-2 text-xs font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
                      >
                        Insert
                      </button>

                      <button
                        type="button"
                        onClick={() => setSignatureManagerOpen(true)}
                        className="min-h-[44px] touch-manipulation rounded bg-btnGeneralNormal px-4 py-2 text-xs font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
                      >
                        Manage…
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 border-t border-gray-200 px-3 py-3 sm:flex sm:justify-between">
                <button
                  type="button"
                  onClick={onClose}
                  className="min-h-[44px] touch-manipulation rounded bg-btnGeneralNormal px-4 py-2 text-xs font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  className="min-h-[44px] touch-manipulation rounded bg-btnActionNormal px-5 py-2 text-xs font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionHover"
                  disabled={isSending}
                >
                  {isSending ? "Sending…" : "Send Email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SignatureManagerModal
        open={signatureManagerOpen}
        onClose={() => setSignatureManagerOpen(false)}
        signatures={signatures}
        onChangeSignatures={handleSignaturesChanged}
        defaultIndex={defaultSignatureIndex}
        onChangeDefaultIndex={handleDefaultSignatureIndexChange}
        theme={theme}
      />

      <EmailConversationModal
        open={!!activeConversationId}
        onClose={() => setActiveConversationId(null)}
        conversationId={activeConversationId}
        jobId={effectiveJobId}
      />
    </>,
    document.body
  );
};

export default EmailMessageModal;
